import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppHttpClientApi } from '../../helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import { SKIP_ERROR_TOAST } from '@core/error-handling/skip-error-toast.context';
import {
  PatientSearchParams,
  PatientSearchBackendResponse,
  SaveSampleByCodePayload,
  ManualEntryPayload,
  ManualEntryResponse,
  ReprocessPayload,
  EntryByCodeResponse
} from './api/patient-api.interface';
import { PatientSearchResult } from '@model/patient/patient.model';
import { PatientSearchMapper } from './mappers/patient.mapper';

export interface SpecimenTypeDto {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly skipToastContext = new HttpContext().set(
    SKIP_ERROR_TOAST,
    true
  );
  private readonly baseUrl = 'patients';

  searchPatient(params: PatientSearchParams): Observable<PatientSearchResult> {
    const httpParams = new HttpParams()
      .set('identifierType', params.identifierType)
      .set('idNumber', params.idNumber);

    return this.httpClient
      .getRaw<PatientSearchBackendResponse>(`${this.baseUrl}/search`, {
        params: httpParams
      })
      .pipe(
        map((response) => PatientSearchMapper.fromBackendDto(response)),
        catchError((error) =>
          this.errorHandler.handleError('searchPatient', error)
        )
      );
  }

  getSpecimenTypes(): Observable<SpecimenTypeDto[]> {
    return this.httpClient
      .getRaw<SpecimenTypeDto[]>('master-data/specimen-types')
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('getSpecimenTypes', error)
        )
      );
  }

  searchByCode(specimenCode: string): Observable<PatientSearchResult> {
    return this.httpClient
      .getRaw<PatientSearchBackendResponse>(
        `orders/entry-by-code/${specimenCode}`
      )
      .pipe(
        map((response) => PatientSearchMapper.fromBackendDto(response)),
        catchError((error) =>
          this.errorHandler.handleError('searchByCode', error)
        )
      );
  }

  saveSampleByCode(
    caseNumber: string,
    payload: SaveSampleByCodePayload
  ): Observable<EntryByCodeResponse> {
    return this.httpClient
      .patchRaw<EntryByCodeResponse>(
        `orders/entry-by-code/${caseNumber}`,
        payload,
        { context: this.skipToastContext }
      )
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('saveSampleByCode', error)
        )
      );
  }

  saveManualSample(
    payload: ManualEntryPayload
  ): Observable<ManualEntryResponse> {
    return this.httpClient
      .postRaw<ManualEntryResponse>('orders/manual-entry', payload, {
        context: this.skipToastContext
      })
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('saveManualSample', error)
        )
      );
  }

  getSampleByCaseNumber(caseNumber: string): Observable<ManualEntryPayload> {
    return this.httpClient
      .getRaw<ManualEntryPayload>(`orders/manual-entry/${caseNumber}`)
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('getSampleByCaseNumber', error)
        )
      );
  }

  updateSample(
    caseNumber: string,
    payload: ManualEntryPayload
  ): Observable<any> {
    return this.httpClient
      .patchRaw<any>(`orders/edit-sample/${caseNumber}`, payload, {
        context: this.skipToastContext
      })
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('updateSample', error)
        )
      );
  }

  deleteSample(specimenCode: string): Observable<void> {
    return this.httpClient
      .delete(`orders/${specimenCode}`)
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('deleteSample', error)
        )
      );
  }

  saveReprocess(
    specimenCode: string,
    payload: ReprocessPayload
  ): Observable<void> {
    return this.httpClient
      .postRaw<void>(`orders/reprocess/${specimenCode}`, payload)
      .pipe(
        catchError((error) =>
          this.errorHandler.handleError('saveReprocess', error)
        )
      );
  }
}
